import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useMutation, QueryClient, QueryClientProvider } from 'react-query';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';

// Importar a função createAppointment
import { createAppointment } from '../../firebase/appointments';

// Componentes modulares
import ServiceSelection from './steps/ServiceSelection';
import DateTimeSelection from './steps/DateTimeSelection';
import ClientForm from './steps/ClientForm';
import ProgressIndicator from './ProgressIndicator';
import SalonHeader from './shared/SalonHeader';
import SuccessMessage from './shared/SuccessMessage';

// Zustand store
import { useBookingStore } from '../../store/bookingStore';

// Esquema de validação
import { bookingSchema } from '../../utils/validationSchemas';

// QueryClient para React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000 // 5 minutos
    }
  }
});

// Componente principal com React Query Provider
const BookingPageWrapper = () => (
  <QueryClientProvider client={queryClient}>
    <BookingPageContainer />
  </QueryClientProvider>
);

// Componente Container com a lógica de negócio
const BookingPageContainer = () => {
  // Parâmetros da URL
  const { salonId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isAdminBooking = queryParams.get('admin') === 'true';

  // Estado para mensagem de sucesso
  const [success, setSuccess] = useState(false);

  // Gerenciamento de estado com Zustand
  const { 
    step,
    resetBooking,
    nextStep,
    prevStep
  } = useBookingStore();

  // Formulário com React Hook Form e validação Zod
  const methods = useForm({
    resolver: zodResolver(bookingSchema),
    mode: 'onChange',
    defaultValues: {
      services: [],
      date: null,
      time: null,
      professional: null,
      client: {
        name: '',
        phone: '',
        email: '',
        notes: ''
      }
    }
  });

  // Resetar o formulário quando mudar de salão
  useEffect(() => {
    resetBooking();
    methods.reset();
  }, [salonId, resetBooking, methods]);

  // Buscar configurações do salão
  const { data: salonConfig, isLoading: salonLoading, error: salonError } = useQuery(
    ['salonConfig', salonId],
    async () => {
      const configRef = collection(db, 'salons');
      const configQuery = query(configRef, where('id', '==', salonId));
      const configSnapshot = await getDocs(configQuery);
      
      if (configSnapshot.empty) {
        throw new Error('Salão não encontrado');
      }
      
      return configSnapshot.docs[0].data();
    }
  );

  // Mutação para enviar agendamento usando a função centralizada
  const submitAppointment = useMutation(
    async (bookingData) => {
      try {
        console.log("Enviando agendamento:", bookingData);
        
        // Preparar o objeto no formato esperado pela API
        const appointmentData = {
          cliente: bookingData.client.name,
          whatsapp: bookingData.client.phone,
          email: bookingData.client.email || "",
          observacoes: bookingData.client.notes || "",
          data: bookingData.date,
          dataFormatada: bookingData.date ? new Date(bookingData.date).toLocaleDateString('pt-BR') : "",
          horario: bookingData.time,
          profissional: typeof bookingData.professional === 'object' 
            ? bookingData.professional.id 
            : bookingData.professional,
          servicos: bookingData.services.map(service => 
            typeof service === 'object' ? service.id : service
          ),
          servicosDetalhes: bookingData.services.map(service => {
            if (typeof service !== 'object') return null;
            return {
              id: service.id,
              nome: service.nome,
              preco: service.preco || 0,
              duracao: service.duracao || 0
            };
          }).filter(Boolean),
          criadoPorAdmin: isAdminBooking,
          origem: isAdminBooking ? 'atendente' : 'cliente',
          deviceType: isAdminBooking ? 'desktop' : 
            (navigator.userAgent.match(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i) 
              ? 'mobile' : 'desktop')
        };
        
        // Usar a função centralizada para criar o agendamento
        const result = await createAppointment(salonId, appointmentData);
        return result;
      } catch (error) {
        console.error('Erro ao submeter agendamento:', error);
        throw error;
      }
    },
    {
      onSuccess: () => {
        // Mostrar mensagem de sucesso
        setSuccess(true);
        
        // Resetar formulário após alguns segundos
        setTimeout(() => {
          setSuccess(false);
          resetBooking();
          methods.reset();
        }, 8000);
      },
      onError: (error) => {
        console.error('Erro ao submeter agendamento:', error);
        alert('Ocorreu um erro ao salvar o agendamento. Por favor, tente novamente.');
      }
    }
  );

  // Handler para submissão do agendamento
  const handleSubmitBooking = async (data) => {
    await submitAppointment.mutateAsync(data);
  };

  // Loading
  if (salonLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <p className="mt-4 text-gray-600">Carregando...</p>
      </div>
    );
  }

  // Erro
  if (salonError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Erro! </strong>
          <span className="block sm:inline">{salonError.message}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: salonConfig?.fontFamily }}>
      {/* Cabeçalho do salão */}
      <SalonHeader salonConfig={salonConfig} />
      
      {/* Conteúdo principal */}
      <main className="container mx-auto py-4 px-4 max-w-md">
        <FormProvider {...methods}>
          {success ? (
            <SuccessMessage 
              onNewBooking={() => setSuccess(false)} 
              salonConfig={salonConfig}
            />
          ) : (
            <>
              {/* Indicador de progresso */}
              <ProgressIndicator 
                currentStep={step} 
                primaryColor={salonConfig?.primaryColor} 
                simpleMode={false}
              />
              
              {/* Etapas do agendamento */}
              {step === 1 && (
                <ServiceSelection 
                  salonId={salonId}
                  salonConfig={salonConfig}
                  onContinue={nextStep}
                  isAdmin={isAdminBooking}
                />
              )}
              
              {step === 2 && (
                <DateTimeSelection 
                  salonId={salonId}
                  salonConfig={salonConfig}
                  onContinue={nextStep}
                  onBack={prevStep}
                  isAdmin={isAdminBooking}
                />
              )}
              
              {step === 3 && (
                <ClientForm 
                  salonId={salonId}
                  salonConfig={salonConfig}
                  onBack={prevStep}
                  onSubmit={handleSubmitBooking}
                  isAdmin={isAdminBooking}
                />
              )}
            </>
          )}
        </FormProvider>
      </main>
      
      <footer className="container mx-auto py-4 px-4 text-center text-gray-500 text-xs mt-8">
        <p>© {new Date().getFullYear()} {salonConfig.name}</p>
      </footer>
    </div>
  );
};

export default BookingPageWrapper; 